// Favoritos de período para las estadísticas de incidencias. Viven en código: cada uno
// sabe calcular su rango a partir de "hoy" y del año elegido en el header de Insights.
// Las semanas van de lunes a domingo, igual que en la API.

export const CUSTOM_PRESET = "custom";
export const MAX_RANGE_DAYS = 366;

const PRESET_STORAGE_KEY = "insights.incidents.periodPreset";

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const startOfWeek = (date) => addDays(date, -((date.getDay() + 6) % 7));

export const toApiDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const fromApiDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const INCIDENT_PERIOD_PRESETS = [
  {
    key: "today",
    label: "dashboard.insights.incidents.presets.today",
    getRange: (today) => ({ from: today, to: today }),
  },
  {
    key: "thisWeek",
    label: "dashboard.insights.incidents.presets.thisWeek",
    getRange: (today) => ({ from: startOfWeek(today), to: today }),
  },
  {
    key: "lastWeek",
    label: "dashboard.insights.incidents.presets.lastWeek",
    getRange: (today) => {
      const from = addDays(startOfWeek(today), -7);
      return { from, to: addDays(from, 6) };
    },
  },
  {
    key: "thisMonth",
    label: "dashboard.insights.incidents.presets.thisMonth",
    getRange: (today) => ({ from: new Date(today.getFullYear(), today.getMonth(), 1), to: today }),
  },
  {
    key: "lastMonth",
    label: "dashboard.insights.incidents.presets.lastMonth",
    getRange: (today) => ({
      from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      to: new Date(today.getFullYear(), today.getMonth(), 0),
    }),
  },
  {
    key: "last30Days",
    label: "dashboard.insights.incidents.presets.last30Days",
    getRange: (today) => ({ from: addDays(today, -29), to: today }),
  },
  {
    // El año del header: completo si ya pasó, hasta hoy si es el actual
    key: "year",
    label: "dashboard.insights.incidents.presets.year",
    getRange: (today, year) => {
      const selectedYear = Number(year) || today.getFullYear();
      const yearEnd = new Date(selectedYear, 11, 31);
      return { from: new Date(selectedYear, 0, 1), to: yearEnd < today ? yearEnd : today };
    },
  },
];

export const DEFAULT_PRESET = "thisMonth";

export const getPresetRange = (key, { today = new Date(), year } = {}) => {
  const preset = INCIDENT_PERIOD_PRESETS.find((item) => item.key === key);
  if (!preset) return null;
  const { from, to } = preset.getRange(startOfDay(today), year);
  return { dateFrom: toApiDate(from), dateTo: toApiDate(to) };
};

// El último favorito elegido se recuerda por navegador; si el storage no está disponible, se usa el default
export const loadPreset = () => {
  try {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY);
    const isKnown = stored === CUSTOM_PRESET || INCIDENT_PERIOD_PRESETS.some((item) => item.key === stored);
    return isKnown ? stored : DEFAULT_PRESET;
  } catch {
    return DEFAULT_PRESET;
  }
};

export const savePreset = (key) => {
  try {
    localStorage.setItem(PRESET_STORAGE_KEY, key);
  } catch {
    // sin storage solo se pierde el recordatorio
  }
};

const languageLocale = () => {
  try {
    return { es: "es", pt: "pt-BR", en: "en-US" }[localStorage.getItem("language")] || "en-US";
  } catch {
    return "en-US";
  }
};

// Etiqueta del eje X según la granularidad que devolvió la API
export const formatBucket = (bucket, granularity) => {
  const date = fromApiDate(bucket);
  const options = granularity === "month" ? { month: "short", year: "numeric" } : { day: "numeric", month: "short" };
  return new Intl.DateTimeFormat(languageLocale(), options).format(date);
};

export const formatRange = (dateFrom, dateTo) => {
  const format = new Intl.DateTimeFormat(languageLocale(), { day: "numeric", month: "short", year: "numeric" });
  return dateFrom === dateTo
    ? format.format(fromApiDate(dateFrom))
    : `${format.format(fromApiDate(dateFrom))} – ${format.format(fromApiDate(dateTo))}`;
};
